/**
 * Where a measurement axis's landmarks go when nothing has placed them yet.
 *
 * The annotation editor can only drag a point that already exists, so a box with
 * no keypoints offers no way to record a measurement. This module owns the two
 * things every seeding caller must agree on: the CANONICAL AXIS ORDER, and the
 * in-bbox placeholder positions.
 *
 * Order is load-bearing, not cosmetic. COCO export flattens an annotation's own
 * keypoint array against `category.keypointNames` with no re-indexing, so an
 * annotation ordered differently from its category silently mislabels every
 * point — e.g. a consumer reading core length as head length. The canonical
 * order matches what the model writes (`MEASUREMENT_KEYPOINT_NAMES` in
 * deferredAnalysisCore): length, then width, then the rest.
 *
 * The placeholder fractions and conventions are the audited ones from
 * `scripts/lib/landmarkRoleRepair.ts`: `visibility` is always 2, placeholders
 * carry NO `confidence` (they are guesses, not measurements, and a confidence
 * would make them look like model output), and the coincident bases
 * `length_start` / `core_length_start` share an exact pixel.
 */
import type { KeypointAnnotation } from "./annotations.js";
import { parseMeasurementEndpointName, type MeasurementDescriptor } from "./measurementDescriptors.js";

export interface SeedBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Axes the model itself emits first, in the order it emits them. */
const LEADING_AXIS_KEYS = ["length", "width"];

type PlacementFraction = { fx: number; fy: number };

/** Audited placeholder positions, as (width, height) fractions of the box. */
const PLACEHOLDER_FRACTIONS: Record<string, { start: PlacementFraction; end: PlacementFraction }> = {
  // vertical, bottom -> top
  length: { start: { fx: 0.5, fy: 0.9 }, end: { fx: 0.5, fy: 0.1 } },
  // horizontal, left -> right
  width: { start: { fx: 0.1, fy: 0.5 }, end: { fx: 0.9, fy: 0.5 } },
  // shares its base with length, running partway up the same line
  core_length: { start: { fx: 0.5, fy: 0.9 }, end: { fx: 0.5, fy: 0.6 } },
};

/**
 * Fallback for an axis this module has no audited placement for: a short
 * vertical segment, stepped across the box by index so several unknown axes
 * cannot land on top of each other. Deliberately never (0,0)/visibility 0 —
 * axis discovery ignores visibility, so that would draw a real axis in the
 * image corner.
 */
export function fallbackFractions(index: number): { start: PlacementFraction; end: PlacementFraction } {
  const fx = Math.min(0.8, 0.2 + 0.15 * index);
  return { start: { fx, fy: 0.85 }, end: { fx, fy: 0.55 } };
}

/**
 * The placeholder axis for ANY key, inside the given box — including a key this
 * module has no audited placement for, and including keys the current trait
 * config does not define. Persisted landmark pairs are authoritative and must
 * never be gated on descriptors, so callers repairing a legacy or custom axis
 * still get a usable in-box position instead of the image corner.
 */
export function seedAxisForKey(
  key: string,
  box: SeedBox,
  fallbackIndex = 0
): { start: { x: number; y: number }; end: { x: number; y: number } } {
  const fractions = PLACEHOLDER_FRACTIONS[key] ?? fallbackFractions(fallbackIndex);
  const at = (endpoint: "start" | "end") => ({
    x: box.x + fractions[endpoint].fx * box.width,
    y: box.y + fractions[endpoint].fy * box.height,
  });
  return { start: at("start"), end: at("end") };
}

/**
 * The placeholder position for one endpoint role (e.g. `core_length_end`), so a
 * caller that works in role names rather than descriptors reads the same table.
 * Returns null for a role this module has no audited placement for.
 */
export function placeholderFractionForRole(role: string): PlacementFraction | null {
  const parsed = parseMeasurementEndpointName(role);
  if (!parsed) return null;
  return PLACEHOLDER_FRACTIONS[parsed.key]?.[parsed.endpoint] ?? null;
}

/**
 * Order axis keys the way `category.keypointNames` must be ordered: the model's
 * leading axes first, then everything else alphabetically so the result is
 * stable across callers and runs.
 */
export function canonicalAxisOrder(keys: readonly string[]): string[] {
  const present = new Set(keys);
  const leading = LEADING_AXIS_KEYS.filter((key) => present.has(key));
  const rest = keys.filter((key) => !LEADING_AXIS_KEYS.includes(key)).sort();
  return [...leading, ...rest];
}

/** The keypoint names for these axes, plus a skeleton indexing into them. */
export function keypointSchemaForAxes(
  keys: readonly string[]
): { keypointNames: string[]; keypointSkeleton: number[][] } | null {
  const ordered = canonicalAxisOrder(keys);
  if (ordered.length === 0) return null;

  return {
    keypointNames: ordered.flatMap((key) => [`${key}_start`, `${key}_end`]),
    keypointSkeleton: ordered.map((_, index) => [index * 2, index * 2 + 1]),
  };
}

/** The measurement axes a trait's descriptors say should carry landmarks. */
export function keypointAxisKeys(descriptors: readonly MeasurementDescriptor[]): string[] {
  return canonicalAxisOrder(descriptors.filter((descriptor) => descriptor.hasKeypoints).map(({ key }) => key));
}

/**
 * Build the placeholder landmarks for a freshly drawn box, in canonical order.
 *
 * Returns [] when the trait has no keypoint-bearing axes (a counting model), so
 * callers can seed unconditionally.
 */
export function buildSeedKeypointsForBox(
  descriptors: readonly MeasurementDescriptor[],
  box: SeedBox
): KeypointAnnotation[] {
  const keys = keypointAxisKeys(descriptors);
  let fallbackIndex = 0;

  return keys.flatMap((key) => {
    const fractions = PLACEHOLDER_FRACTIONS[key] ?? fallbackFractions(fallbackIndex++);
    return (["start", "end"] as const).map((endpoint) => {
      const { fx, fy } = fractions[endpoint];
      return {
        name: `${key}_${endpoint}`,
        x: box.x + fx * box.width,
        y: box.y + fy * box.height,
        visibility: 2,
      };
    });
  });
}
